import { useCallback, useEffect, useRef, useState } from 'react';

// Locale codes for Chrome's speech engine. 'bn' -> Bangladeshi Bangla.
const LOCALE = { en: 'en-US', bn: 'bn-BD' };

// Join freshly dictated text onto the field's pre-dictation value (space-separated).
const joinBase = (base, t) => (base && base.trim() ? `${base.trim()} ${t}` : t);

function getRecognitionCtor() {
    if (typeof window === 'undefined') return null;
    return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

/**
 * Browser-native voice dictation (Web Speech API). Only one field records at a
 * time. Chromium-only (Chrome/Edge); `supported` is false elsewhere so callers
 * can disable their mic buttons.
 *
 * Live (interim) results: as the user speaks, the whole session transcript is
 * rebuilt and written to the field via `setValue` — fast, word-by-word typing.
 *
 * Usage:
 *   const voice = useVoiceDictation();
 *   voice.toggle('question', questionText, setQuestionText);
 *   // baseValue = field value when the mic was clicked; setValue writes the field.
 */
export default function useVoiceDictation() {
    const Ctor = getRecognitionCtor();
    const supported = !!Ctor;

    const [language, setLanguage] = useState('en'); // 'en' | 'bn'
    const [listeningField, setListeningField] = useState(null);

    // Live refs so the recognition event handlers never read stale values.
    const recognitionRef = useRef(null);
    const baseRef = useRef('');
    const setValueRef = useRef(null);
    const fieldRef = useRef(null);
    const languageRef = useRef(language);
    languageRef.current = language;

    const stop = useCallback(() => {
        const rec = recognitionRef.current;
        if (rec) {
            try {
                rec.onend = null; // avoid the onend handler racing our reset
                rec.stop();
            } catch (e) {
                /* ignore */
            }
            recognitionRef.current = null;
        }
        baseRef.current = '';
        setValueRef.current = null;
        fieldRef.current = null;
        setListeningField(null);
    }, []);

    const start = useCallback((fieldKey, baseValue, setValue) => {
        if (!Ctor) return;
        // Tear down any in-flight session before starting a new one.
        if (recognitionRef.current) {
            try {
                recognitionRef.current.onend = null;
                recognitionRef.current.stop();
            } catch (e) {
                /* ignore */
            }
            recognitionRef.current = null;
        }

        const rec = new Ctor();
        rec.lang = LOCALE[languageRef.current] || LOCALE.en;
        rec.continuous = true;
        rec.interimResults = true;

        rec.onresult = (event) => {
            // Rebuild the whole session transcript (final + still-interim pieces)
            // every event so live updates replace—not duplicate—prior text.
            const pieces = [];
            for (let i = 0; i < event.results.length; i += 1) {
                const tx = (event.results[i][0]?.transcript || '').trim();
                if (tx) pieces.push(tx);
            }
            const sessionText = pieces.join(' ');
            if (setValueRef.current) {
                setValueRef.current(joinBase(baseRef.current, sessionText));
            }
        };
        rec.onend = () => {
            // Fires on the browser's auto-stop after silence — reset to idle.
            recognitionRef.current = null;
            baseRef.current = '';
            setValueRef.current = null;
            fieldRef.current = null;
            setListeningField(null);
        };
        rec.onerror = () => {
            recognitionRef.current = null;
            baseRef.current = '';
            setValueRef.current = null;
            fieldRef.current = null;
            setListeningField(null);
        };

        recognitionRef.current = rec;
        baseRef.current = baseValue || '';
        setValueRef.current = setValue;
        fieldRef.current = fieldKey;
        try {
            rec.start();
            setListeningField(fieldKey);
        } catch (e) {
            stop();
        }
    }, [Ctor, stop]);

    const toggle = useCallback((fieldKey, baseValue, setValue) => {
        if (fieldRef.current === fieldKey) {
            stop();
        } else {
            start(fieldKey, baseValue, setValue);
        }
    }, [start, stop]);

    // Abort any active recognition when the component unmounts.
    useEffect(() => stop, [stop]);

    return { supported, language, setLanguage, listeningField, toggle, stop };
}
